import { has, path } from "ramda";
import { from, of, switchMap } from "rxjs";
import { catchError, filter, map, toArray } from "rxjs/operators";

import { Query } from "../../types/firebase.types";
import { GenericObject } from "../../types/generic.types";
import { IGetListParams } from "../../types/reactAdmin.types";

const MAX_DATE_LIMIT = 8640000000000000;
const MIN_DATE_LIMIT = -8640000000000000;

export const getSearchResults = (query: Query, value: string, source: string) => {
  return from(query.get()).pipe(
    switchMap((snapshot) => from(snapshot.docs)),
    map((doc) => ({
      ...doc.data(),
      id: doc.id,
    })),
    filter((result: GenericObject) => {
      return (path(source.split("."), result) as any).includes(value);
    }),
    toArray(),
    catchError((ex: any) => {
      console.log(ex.message);
      return of([]);
    })
  );
};

/**
 * used in init/resources directory to handle any filter range
 * */
export const prepareRangeFilterProps = (params: IGetListParams, source: string) => {
  if (has(`${source}From`)(params.filter)) {
    (params.filter as any)[source] = path([`${source}From`])(params.filter) || 0;
    params.operators[source] = ">=";
    params.sort = {
      field: source,
      order: params.sort.order,
    };
    delete params.filter[`${source}From`];
  }
  if (has(`${source}To`)(params.filter)) {
    (params.additionalFilter as any)[source] = path([`${source}To`])(params.filter) || Number.MAX_SAFE_INTEGER;
    params.additionalOperators[source] = "<=";
    params.sort = {
      field: source,
      order: params.sort.order,
    };
    delete params.filter[`${source}To`];
  } else {
    delete params.additionalOperators[source];
    delete params.additionalFilter[source];
  }

  return params;
};

/**
 * this function prepare params for firestore query in case if we have custom range filters
 * we can use it instead of `prepareRangeFilterProps`
 * it's useless for now as it generate params that doesn't fit firestore query restrictions
 * */
export const prepareRangeFiltersProps = (params: IGetListParams, sources: string[]) => {
  const temp = sources.reduce(
    (acc, source) => {
      if (has(`${source}From`)(params.filter)) {
        const value = path<string | number>([`${source}From`])(params.filter);
        acc.rangeFilter = {
          ...acc.rangeFilter,
          [source]: typeof value === "string" && value.includes("-") ? new Date(value || MIN_DATE_LIMIT) : value || 0,
        };
        acc.rangeOperators = {
          ...acc.rangeOperators,
          [source]: ">=",
        };
        params.sort = {
          field: source,
          order: params.sort.order,
        };
        delete params.filter[`${source}From`];
      }

      if (has(`${source}To`)(params.filter)) {
        const value = path([`${source}To`])(params.filter) || Number.MAX_SAFE_INTEGER;
        acc.rangeAdditionalFilter = {
          ...acc.rangeAdditionalFilter,
          [source]:
            typeof value === "string" && value.includes("-")
              ? new Date(value || MAX_DATE_LIMIT)
              : value || Number.MAX_SAFE_INTEGER,
        };
        acc.rangeAdditionalOperators = {
          ...acc.rangeAdditionalOperators,
          [source]: "<=",
        };
        params.sort = {
          field: source,
          order: params.sort.order,
        };
        delete params.filter[`${source}To`];
      } else {
        delete params.additionalOperators[source];
        delete params.additionalFilter[source];
      }

      return acc;
    },
    {
      rangeFilter: {},
      rangeOperators: {},
      rangeAdditionalFilter: {},
      rangeAdditionalOperators: {},
    }
  );

  return {
    ...params,
    filter: {
      ...params.filter,
      ...temp.rangeFilter,
    },
    additionalFilter: {
      ...params.additionalFilter,
      ...temp.rangeAdditionalFilter,
    },
    operators: {
      ...params.operators,
      ...temp.rangeOperators,
    },
    additionalOperators: {
      ...params.additionalOperators,
      ...temp.rangeAdditionalOperators,
    },
  };
};
